import React from 'react';
import { Check, ChevronDown } from 'lucide-react';
import { cn } from '../ui/utils';
import type {
  PerformanceCategory,
  PerformanceGoal,
  PerformancePlan,
  PerformanceSchedule,
  PerformanceTimelineItem,
} from '../../types/performance.types';
import {
  performancePillClass,
  type PerformanceTone,
  weightSegments,
} from '../../utils/performance';

export const PerformancePill: React.FC<{
  tone?: PerformanceTone | string;
  children: React.ReactNode;
  className?: string;
}> = ({ tone = 'muted', children, className }) => (
  <span
    className={cn(
      'inline-flex items-center whitespace-nowrap rounded-full border px-2 py-0.5 text-xs font-medium',
      performancePillClass(tone),
      className
    )}
  >
    {children}
  </span>
);

export const InitialsAvatar: React.FC<{ name: string; className?: string }> = ({
  name,
  className,
}) => (
  <span
    className={cn(
      'grid h-8 w-8 shrink-0 place-items-center rounded-full bg-muted text-xs font-semibold text-foreground',
      className
    )}
  >
    {String(name || '')
      .split(/\s+/)
      .filter(Boolean)
      .map((part) => part[0])
      .join('')
      .slice(0, 2)
      .toUpperCase()}
  </span>
);

export const PerformanceSectionHeading: React.FC<{
  title: string;
  description?: string;
  action?: React.ReactNode;
}> = ({ title, description, action }) => (
  <div className="flex flex-wrap items-start justify-between gap-3">
    <div>
      <h2 className="text-base font-semibold text-foreground">{title}</h2>
      {description ? (
        <p className="mt-0.5 text-sm text-muted-foreground">{description}</p>
      ) : null}
    </div>
    {action}
  </div>
);

export const PerformanceEmptyState: React.FC<{
  title: string;
  body?: string;
  action?: React.ReactNode;
}> = ({ title, body, action }) => (
  <div className="rounded-xl border border-dashed border-border bg-card p-10 text-center">
    <p className="text-sm font-semibold text-foreground">{title}</p>
    {body ? <p className="mx-auto mt-1 max-w-lg text-sm text-muted-foreground">{body}</p> : null}
    {action ? <div className="mt-4 flex justify-center">{action}</div> : null}
  </div>
);

/**
 * Six date-derived phases. Done phases are green, the current one is the navy primary,
 * upcoming are muted. This is the prototype's PhaseTimeline, rebuilt with tokens.
 */
export const PhaseTimeline: React.FC<{ timeline: PerformanceTimelineItem[] }> = ({
  timeline,
}) => (
  <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
    {timeline.map((item) => {
      const done = item.state === 'done';
      const current = item.state === 'current';
      return (
        <div
          key={item.key}
          className={cn(
            'rounded-[10px] border px-3 py-2.5 transition-colors duration-200',
            done && 'border-link bg-card',
            current && 'border-link bg-selection',
            !done && !current && 'border-border bg-card'
          )}
        >
          <div
            className={cn(
              'text-xs font-semibold',
              (done || current) && 'text-link',
              !done && !current && 'text-muted-foreground'
            )}
          >
            {item.label}
          </div>
          <div
            className={cn(
              'mt-1 text-[11px] tabular-nums',
              (done || current) && 'text-link',
              !done && !current && 'text-muted-foreground'
            )}
          >
            {item.range}
          </div>
          {current && item.key !== 'released' ? (
            <div className="mt-1 text-[11px] font-medium text-link">
              Today
            </div>
          ) : null}
        </div>
      );
    })}
  </div>
);

/**
 * Stacked category-coloured weighting bar. Widths are relative to max(total, 100) so an
 * over-allocated plan shows the overflow rather than silently rescaling to 100%.
 */
export const WeightingBar: React.FC<{
  goals: PerformanceGoal[];
  highlightId?: number | null;
  className?: string;
}> = ({ goals, highlightId, className }) => {
  const segments = weightSegments(goals);
  const total = goals
    .filter((goal) => goal.status !== 'Withdrawn')
    .reduce((sum, goal) => sum + (Number(goal.weight) || 0), 0);

  return (
    <div className={className}>
      <div className="flex items-center justify-between gap-3">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          Weighting
        </span>
        <span
          className={cn(
            'inline-flex items-center gap-1 text-xs font-semibold tabular-nums',
            total === 100 ? 'text-success' : 'text-warning'
          )}
        >
          {total === 100 ? <Check className="h-3.5 w-3.5" /> : null}
          {total}%
        </span>
      </div>
      <div className="mt-2 flex h-2.5 w-full overflow-hidden rounded-full bg-muted">
        {segments.map((segment) => (
          <span
            key={segment.id}
            title={segment.title}
            className="h-full transition-opacity"
            style={{
              width: segment.width,
              backgroundColor: segment.colour,
              opacity: highlightId && highlightId !== segment.id ? 0.55 : 1,
            }}
          />
        ))}
      </div>
    </div>
  );
};

export const PlanChecklist: React.FC<{
  plan: PerformancePlan;
  schedule: PerformanceSchedule;
  onSubmit: () => void;
  submitting?: boolean;
}> = ({ plan, schedule, onSubmit, submitting }) => {
  const readyToSubmit = plan.ready;
  return (
  <div
    className={cn(
      'rounded-[10px] border p-4',
      readyToSubmit ? 'border-destructive bg-destructive-bg' : 'border-border bg-card'
    )}
  >
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div>
        <p
          className={cn(
            'text-sm font-semibold',
            readyToSubmit ? 'text-destructive' : 'text-foreground'
          )}
        >
          Goal plan checklist
        </p>
        <p className="text-xs text-muted-foreground">
          {schedule.minGoals}–{schedule.maxGoals} goals, each {schedule.minWeight}–{schedule.maxWeight}%,
          totalling 100%.
        </p>
        {readyToSubmit ? (
          <p className="mt-1 text-xs font-medium text-destructive">
            Submit goal plan to finalise.
          </p>
        ) : null}
      </div>
      <button
        type="button"
        onClick={onSubmit}
        disabled={!plan.ready || submitting}
        className="inline-flex h-10 items-center rounded-md bg-primary px-5 text-sm font-semibold text-primary-foreground disabled:pointer-events-none disabled:opacity-50"
      >
        {submitting ? 'Submitting…' : 'Submit goal plan'}
      </button>
    </div>
    <ul className="mt-3 grid gap-2 sm:grid-cols-3">
      {plan.checks.map((check) => (
        <li key={check.key} className="flex items-start gap-2 text-xs">
          <span
            className={cn(
              'mt-0.5 grid h-4 w-4 shrink-0 place-items-center rounded-full border',
              check.ok
                ? 'border-success-border bg-success-bg text-success'
                : 'border-border bg-muted text-muted-foreground'
            )}
          >
            {check.ok ? <Check className="h-3 w-3" /> : null}
          </span>
          <span>
            <span className={cn('font-medium', check.ok ? 'text-foreground' : 'text-muted-foreground')}>
              {check.label}
            </span>
            <span className="block text-muted-foreground">{check.detail}</span>
          </span>
        </li>
      ))}
    </ul>
  </div>
  );
};

export const CollapsibleSection: React.FC<{
  title: string;
  meta?: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
  action?: React.ReactNode;
  className?: string;
}> = ({ title, meta, children, defaultOpen = false, action, className }) => {
  const [open, setOpen] = React.useState(defaultOpen);
  return (
    <div className={cn('overflow-hidden rounded-xl border border-border bg-card', className)}>
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left transition-colors duration-200 hover:bg-accent/60"
      >
        <span className="flex min-w-0 items-center gap-2">
          <ChevronDown
            className={cn('h-4 w-4 shrink-0 text-muted-foreground transition-transform', !open && '-rotate-90')}
          />
          <span className="text-sm font-semibold text-foreground">{title}</span>
          {meta ? <span className="text-xs text-muted-foreground">{meta}</span> : null}
        </span>
        {action}
      </button>
      {open ? (
        <div className="border-t border-border px-4 py-3">{children}</div>
      ) : null}
    </div>
  );
};

export const CategoryPill: React.FC<{ category: PerformanceCategory | { name: string; colour: string } }> = ({
  category,
}) => (
  <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
    <span
      className="h-2 w-2 rounded-full"
      style={{ backgroundColor: (category as PerformanceCategory).colour || '#717182' }}
    />
    {(category as PerformanceCategory).name}
  </span>
);
